'use client';

import CloseIcon from '@mui/icons-material/Close';
import {Box, styled} from '@mui/material';
import {StyledSectionContainer} from '@/components/sections/shared/Section.styles';

export const StyledPopupSectionContainer = styled(StyledSectionContainer)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

export const StyledPopupRowWrapper = styled(Box)(() => ({
  position: 'relative',
}));

type StyledPopupRowProps = {
  columns: number;
  hasExpanded?: boolean;
};

export const StyledPopupRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'columns' && prop !== 'hasExpanded',
})<StyledPopupRowProps>(({theme, columns, hasExpanded}) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${columns}, 1fr)`,
  gap: 24,
  [theme.breakpoints.down('sm')]: {
    ...(hasExpanded && { display: 'none' }),
  },
}));

export const StyledPopupCard = styled('button')(({theme}) => ({
  // reset native button defaults
  appearance: 'none',
  background: 'transparent',
  border: 'none',
  padding: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'inherit',

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  cursor: 'pointer',
  '& img': {
    transition: 'transform 400ms ease',
  },
  '&:hover img': {
    transform: 'scale(1.03)',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 4,
    borderRadius: theme.shape.borderRadiusMd,
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& img': {
      transition: 'none',
    },
    '&:hover img': {
      transform: 'none',
    },
  },
}));

export const StyledPopupCardImageWrapper = styled(Box)(() => ({
  position: 'relative',
  width: 250,
  paddingTop: 10,
  '@media (max-width: 600px)': {
    width: 180,
  },
}));

export const StyledPopupCardImageContainer = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',
  borderRadius: '50%',
  overflow: 'hidden',
  zIndex: 2,
}));

export const StyledPopupBadge = styled(Box)(({theme}) => ({
  position: 'absolute',
  top: 3,
  right: 3,
  width: 75,
  height: 75,
  borderRadius: '50%',
  backgroundColor: theme.palette.background.grey,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
  cursor: 'pointer',
  '@media (max-width: 600px)': {
    top: -1,
    right: -1,
  },
}));

export const StyledPopupBadgeIconContainer = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  height: '100%',
}));

export const StyledPopupBadgeIcon = styled(CloseIcon)(({theme}) => ({
  transform: 'rotate(45deg)',
  color: theme.palette.text.dark,
  position: 'absolute',
  top: 18,
  right: 18,
  borderRadius: '50%',
  border: `1.5px solid ${theme.palette.background.dark}`,
  '@media (max-width: 600px)': {
    top: 12,
    right: 12,
  },
}));

export const StyledPopupExpandedRow = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(4),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.grey,
  borderRadius: theme.shape.borderRadiusLg,
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10,
  overflow: 'auto',
  animation: 'expandedReveal 400ms ease-out',
  '@keyframes expandedReveal': {
    from: {
      opacity: 0,
      clipPath: 'inset(0 100% 0 0 round 8px)',
    },
    to: {
      opacity: 1,
      clipPath: 'inset(0 0 0 0 round 8px)',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    position: 'relative',
    bottom: 'auto',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    overflow: 'visible',
  },
}));

export const StyledPopupExpandedImageWrapper = styled(Box)(() => ({
  position: 'relative',
  width: 200,
  minWidth: 200,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  '@media (max-width: 600px)': {
    width: 150,
    minWidth: 150,
  },
}));

export const StyledPopupExpandedImageContainer = styled(Box)(() => ({
  position: 'relative',
  width: 210,
  height: 210,
  flexShrink: 0,
  borderRadius: '50%',
  overflow: 'hidden',
  cursor: 'pointer',
  '@media (max-width: 600px)': {
    width: 160,
    height: 160,
  },
}));

export const StyledPopupExpandedContent = styled(Box)(({theme}) => ({
  flex: 1,
  maxHeight: 280,
  overflowY: 'auto',
  minWidth: 0,
  color: theme.palette.text.dark,
  animation: 'contentFadeIn 300ms ease-out 200ms both',
  '@keyframes contentFadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    maxHeight: 'none',
    overflowY: 'visible',
  },
}));

export const StyledPopupCloseButton = styled('button')(({theme}) => ({
  // reset native button defaults
  appearance: 'none',
  padding: 0,
  font: 'inherit',

  position: 'absolute',
  top: theme.spacing(1.5),
  right: theme.spacing(1.5),
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: theme.palette.background.grey,
  border: `1px solid ${theme.palette.text.dark}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 1,
  '&:hover': {
    backgroundColor: theme.palette.grey[400],
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));
